<template>
  <div v-if="open" class="tm-backdrop" role="dialog" aria-modal="true" aria-labelledby="bc-title" @click.self="close">
    <div class="tm-modal">
      <div class="tm-modal-header">
        <div>
          <h5 id="bc-title" class="mb-0 font-weight-bold text-white"><i class="fe fe-video mr-2"></i>Book a telemedicine consultation</h5>
          <small class="text-white" style="opacity:.8;">With one of your patients, in one of your doctors' free times</small>
        </div>
        <button type="button" class="tm-close" aria-label="Close" @click="close"><i class="fe fe-x"></i></button>
      </div>

      <form class="tm-modal-body" @submit.prevent="submit">
        <div class="bc-fee" :class="{ 'bc-fee--free': !fee }">
          <i class="fe fe-credit-card mr-2"></i>
          <span v-if="fee">The patient pays <strong>₦{{ fee.toLocaleString("en-NG") }}</strong> online from the link in their email before joining.</span>
          <span v-else>Your consultations are free. Set a fee on <router-link to="/my-practice">My practice</router-link>.</span>
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="bc-search" class="tm-label">Search patient</label>
            <input id="bc-search" v-model="q" type="search" class="form-control" placeholder="Name, ID number or phone…" @input="search" />
          </div>
          <div class="form-group col-md-6">
            <label for="bc-patient" class="tm-label">Patient <span class="text-danger">*</span>
              <span v-if="searching" class="text-muted small ml-1">searching…</span>
            </label>
            <select id="bc-patient" v-model="patient" class="form-control" required>
              <option :value="null">-- select --</option>
              <option v-for="p in results" :key="p.type + (p.dependent_id || p.patient_id)" :value="p">
                {{ p.name }} ({{ p.id_card_number }}){{ p.type === "Dependent" ? " · dependant" : "" }}
              </option>
            </select>
            <small v-if="patient && !patient.has_email" class="text-warning">
              This patient has no email address, so they won't get the link by email. Share it from the consultation page.
            </small>
          </div>
        </div>

        <div class="form-group" v-if="doctorList.length > 1">
          <label for="bc-doctor" class="tm-label">Doctor <span class="text-muted small">(optional)</span></label>
          <select id="bc-doctor" v-model="professionalId" class="form-control">
            <option :value="null">Any free doctor</option>
            <option v-for="d in doctorList" :key="d.id" :value="d.id">{{ d.name }}</option>
          </select>
        </div>

        <SlotPicker v-model="slot" :facility-id="facilityId" :professional-id="professionalId" :initial-date="today" />

        <div class="form-group">
          <label for="bc-reason" class="tm-label">Reason for the consultation <span class="text-danger">*</span></label>
          <textarea id="bc-reason" v-model="reason" rows="3" class="form-control" required></textarea>
        </div>

        <div v-if="error" class="alert alert-danger small py-2">{{ error }}</div>

        <div class="d-flex justify-content-end" style="gap: 8px;">
          <button type="button" class="btn btn-outline-secondary" @click="close">Cancel</button>
          <button type="submit" class="tm-btn" :disabled="saving || !patient || !slot || !reason.trim()">
            <span v-if="saving" class="spinner-border spinner-border-sm mr-1" role="status"></span>
            Book consultation
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
// Independent facilities book their own patients (backend Independent\ConsultationController).
import SlotPicker from "@/views/telemedicine/SlotPicker.vue";
import { independent } from "@/service/independent";

export default {
  name: "BookConsultation",
  components: { SlotPicker },
  props: {
    open: { type: Boolean, default: false },
    // Pre-selected patient: { type, patient_id, dependent_id, name, id_card_number, has_email }
    preset: { type: Object, default: null },
  },
  data() {
    const row = independent();
    return {
      facilityId: row ? row.facility_id : null,
      fee: row ? Number(row.consultation_fee) || 0 : 0,
      today: new Date().toISOString().slice(0, 10),
      q: "",
      results: [],
      searching: false,
      timer: null,
      patient: null,
      doctorList: [],
      professionalId: null,
      slot: null,
      reason: "",
      saving: false,
      error: "",
    };
  },
  watch: {
    open(isOpen) {
      if (!isOpen) return;
      this.error = "";
      this.reason = "";
      this.slot = null;
      const row = independent();
      this.fee = row ? Number(row.consultation_fee) || 0 : 0;
      if (this.preset) {
        this.results = [this.preset];
        this.patient = this.preset;
      } else {
        this.patient = null;
        this.search();
      }
      this.loadDoctors();
    },
  },
  methods: {
    close() {
      this.$emit("close");
    },
    search() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.searching = true;
        this.axios
          .get("/api/v1/auth/independent/patients", { params: { q: this.q } })
          .then((res) => { this.results = res.data.data || []; })
          .catch(() => { this.results = []; })
          .finally(() => { this.searching = false; });
      }, 300);
    },
    loadDoctors() {
      // The facility account and its staff with consultation hours (slots carry the doctor's name).
      if (!this.facilityId) return;
      const to = new Date(Date.now() + 13 * 86400000).toISOString().slice(0, 10);
      this.axios
        .get("/api/v1/auth/telemedicine/slots", { params: { provider_id: this.facilityId, from: this.today, to } })
        .then((res) => {
          const byId = {};
          (res.data.data || []).forEach((s) => { byId[s.professional_id] = s.doctor; });
          this.doctorList = Object.keys(byId).map((id) => ({ id: Number(id), name: byId[id] }));
        })
        .catch(() => { this.doctorList = []; });
    },
    submit() {
      this.error = "";
      this.saving = true;
      this.axios
        .post("/api/v1/auth/independent/consultations", {
          patient_id: this.patient.patient_id,
          dependent_id: this.patient.dependent_id,
          professional_id: this.slot.professional_id,
          appointDate: this.slot.date,
          appoinTime: this.slot.time,
          reason: this.reason,
        })
        .then((res) => {
          this.$toasted.success(res.data.message, { position: "top-center", duration: 5000 });
          this.$emit("booked", res.data.data);
          this.close();
        })
        .catch((err) => {
          const body = (err.response && err.response.data) || {};
          this.error = body.errors ? Object.values(body.errors).flat().join(" ") : body.message || "Could not book the consultation.";
        })
        .finally(() => { this.saving = false; });
    },
  },
};
</script>

<style scoped>
.tm-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, .55); z-index: 1050; display: flex; align-items: flex-start; justify-content: center; padding: 40px 12px; overflow-y: auto; }
.tm-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 720px; overflow: hidden; box-shadow: 0 20px 50px rgba(0,0,0,.25); }
.tm-modal-header { display: flex; justify-content: space-between; align-items: flex-start; padding: 20px 24px; background: linear-gradient(135deg, #4c1d95, #7c3aed); }
.tm-close { background: rgba(255,255,255,.15); border: none; border-radius: 8px; color: #fff; width: 32px; height: 32px; }
.tm-modal-body { padding: 20px 24px; }
.tm-label { font-weight: 600; font-size: .85rem; color: #374151; }
.tm-btn { background: linear-gradient(135deg, #4c1d95, #7c3aed); color: #fff; border: none; border-radius: 8px; font-weight: 600; padding: 8px 20px; }
.tm-btn:hover { opacity: .9; color: #fff; }
.tm-btn:disabled { opacity: .5; }
.bc-fee { background: #f5f3ff; color: #4c1d95; border-radius: 10px; padding: 10px 14px; font-size: .88rem; margin-bottom: 16px; }
.bc-fee--free { background: #f8fafc; color: #475569; }
</style>
